import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import { getEssay } from '../../data/essays';
import { ArrowLeft } from 'lucide-react';

const essay = getEssay('oversight-degrades-the-overseer')!;

const OversightDegradesTheOverseer = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": essay.title,
    "description": essay.subtitle,
    "datePublished": essay.date,
    "dateModified": essay.dateModified ?? essay.date,
    "author": {
      "@type": "Person",
      "name": essay.author,
      "url": "https://www.linkedin.com/in/kent-frazier/",
    },
    "publisher": {
      "@type": "Organization",
      "name": "ParadoxEdge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.paradoxedge.com/lovable-uploads/180210a9-9a73-4038-a36b-f373598ef48a.png",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.paradoxedge.com/essays/${essay.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${essay.title} | Essays`}
        description={essay.subtitle}
        canonicalUrl={`/essays/${essay.slug}`}
        ogType="article"
        publishedTime={essay.date}
        modifiedTime={essay.dateModified ?? essay.date}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <main className="flex-1">
          <article className="py-20 sm:py-28">
            <div className="container-mobile max-w-[42rem]">
              {/* Back link */}
              <Link
                to="/essays"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 text-sm text-foreground/90 hover:text-primary transition-colors mb-10"
              >
                <ArrowLeft className="w-4 h-4" />
                All essays
              </Link>

              {/* Header */}
              <header className="mb-12 sm:mb-16">
                <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-primary font-semibold mb-6">
                  Essay
                </p>
                <h1 className="mb-6">{essay.title}</h1>
                <p className="font-display text-xl sm:text-2xl text-foreground/95 leading-snug mb-8 italic">
                  {essay.subtitle}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground border-t border-border pt-6">
                  <span className="font-semibold text-foreground">By {essay.author}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={essay.date}>{essay.dateDisplay}</time>
                  <span aria-hidden="true">·</span>
                  <span>{essay.readingMinutes} min read</span>
                </div>
              </header>

              {/* Body */}
              <div className="essay-prose">
                <p>What if the “human in the loop” who is managing the output of the machine is being degraded by the loop itself?</p>

                <p>If you have been integrating AI into your work, what are you noticing about yourself as you oversee output you did not author?</p>

                <p>What I hear is that attention degrades, skills atrophy, disagreement disappears, and people approve what they have not read and do not fully understand.</p>

                <p>Researchers at Hugging Face and Data &amp; Society found the same in a recent position paper. Their line for it is that oversight degrades the overseer.</p>

                <p>This is not new news.</p>

                <p>In 1983, a human factors researcher named Lisanne Bainbridge described this observation and called it “the irony of automation” while studying industrial control rooms.</p>

                <p>The more capable the machine becomes, the more the human’s skill and situational awareness erode, and eventually the person is least prepared to respond skillfully at the exact moment that it's most needed.</p>

                <p>Aviation has known this for forty years.</p>

                <p>The human in the loop is a safeguard. The regulations require it. Vendors point to it. Boards are reassured when they ask who is watching. Every assurance is resting on a person being there to catch it.</p>

                <p>That person is being worn down by the same use the safeguard depends on.</p>

                <p>So why do we think this will fly in the age of AI, and what choices do we have in our response?</p>

                <p>The proposals on the table are to create friction in the interface. Batch the approvals. Rotate people off the task. Enforce mandatory breaks. Do the work manually occasionally to not lose your edge. Create “canaries” to detect when your judgment is slipping. These are all sound and worth doing.</p>

                <p>They handle volume. They do not develop judgment.</p>

                {/* Section break — the pivot of the piece */}
                <div className="not-prose my-16 sm:my-20 flex items-center justify-center gap-6">
                  <span className="h-px w-20 bg-primary/30" />
                  <span className="inline-block w-2 h-2 bg-primary/50 rotate-45" />
                  <span className="h-px w-20 bg-primary/30" />
                </div>

                <p>Where is the consideration or proposal that human beings can be developed?</p>

                <p>What if we were to imagine the person coming out of this transformation with more capacity and more capability than they had when they started?</p>

                <p>So we upgrade the machine and we schedule a break for the human being?</p>

                <p>Are we designing in a way that turns people into widgets whose diminishing capabilities need to be managed?</p>

                <p>An interface can be built in months. A person who continues to develop takes years and is not something to ship. So the investment goes to what can be built and shipped quickly, and the person lands in policy and training.</p>

                <p>Approval is not oversight.</p>

                <p>Judgment, discernment, wisdom, and accountability are not binary, either turned on or off. These capacities grow. A person who can remain present under pressure, hold multiple competing perspectives, and stand behind what they authorize is a different person in that seat than they were a year ago, and no machine interface develops that.</p>

                <p>Developing human beings is a safety strategy.</p>

                <p>The human being is the element in this system capable of development. Everything else gets replaced by a “better” version.</p>

                <p>We can become more. This moment is asking that of us.</p>

                <p>So notice what you are noticing.</p>

                <p>Who is investing in you, and are you willing to wait?</p>
              </div>

              {/* Sources */}
              <div className="not-prose mt-16 sm:mt-20 pt-10 border-t border-primary/20">
                <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-4">
                  Sources
                </p>
                <div className="space-y-4 text-sm text-foreground/80 leading-relaxed">
                  <p>
                    Margaret Mitchell, Avijit Ghosh, and Samir Passi, “AI Agents Push Humans Out of the Loop,” arXiv preprint arXiv:2608.23642 (24 August 2026).{' '}
                    <a
                      href="https://arxiv.org/abs/2608.23642"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      https://arxiv.org/abs/2608.23642
                    </a>
                  </p>
                  <p>
                    Lisanne Bainbridge, “Ironies of Automation,” Automatica 19, no. 6 (November 1983): 775–779. doi:{' '}
                    <a
                      href="https://doi.org/10.1016/0005-1098(83)90046-8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      10.1016/0005-1098(83)90046-8
                    </a>
                  </p>
                </div>
              </div>

              {/* Author block */}
              <aside className="mt-20 pt-10 border-t border-border">
                <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-4">
                  About the author
                </p>
                <p className="text-foreground leading-relaxed">
                  Kent Frazier is the founder of ParadoxEdge. A former HR executive and Certified Integral Master Coach, he has spent three decades inside the work of leadership, partnering with senior leaders to develop the inner capacities and outer capabilities that meet the moment.
                </p>
                <div className="mt-8">
                  <a
                    href="https://calendly.com/kentfrazier/30-minutes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 text-sm font-semibold rounded-full transition-all duration-300"
                  >
                    Begin a Conversation
                  </a>
                </div>
              </aside>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default OversightDegradesTheOverseer;
